import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import styled from 'styled-components';
import { Modal, Button } from 'react-bootstrap';

// Styled Components
const StyledModal = styled(Modal)`
	color: ${({ theme }) => theme.colors.textColor};

	.btn-close {
		color: ${({ theme }) => theme.colors.textColor};
	}

	.modal-header {
		border-bottom: 1px solid ${({ theme }) => theme.colors.border};
	}

	.modal-footer {
		border-top: 1px solid ${({ theme }) => theme.colors.border};
	}

	.modal-content {
		background-color: ${({ theme }) => theme.colors.background};
	}
`;

// Context
const ConfirmContext = createContext(null);
ConfirmContext.displayName = 'ConfirmContext'; // For debugging and DevTools clarity

// Hook
export const useConfirm = () => {
	const context = useContext(ConfirmContext);

	if (!context) {
		throw new Error('useConfirm must be used within a ConfirmProvider');
	}

	return context;
};

// Provider
export const ConfirmProvider = ({ children }) => {
	const [options, setOptions] = useState(null);
	const resolverRef = useRef(null);

	useEffect(() => {
		return () => {
			if (resolverRef.current) {
				resolverRef.current(false);
				resolverRef.current = null;
			}
		};
	}, []);

	const confirm = useCallback((confirmOptions = {}) => {
		if (!confirmOptions.message) {
			return Promise.reject('"message" is required');
		}

		if (resolverRef.current !== null) {
			console.warn('A confirmation dialog is already open');
			return Promise.resolve(false);
		}

		return new Promise(resolve => {
			resolverRef.current = resolve;
			setOptions(confirmOptions);
		});
	}, []);

	const close = useCallback(result => {
		if (resolverRef.current) {
			resolverRef.current(result);
			resolverRef.current = null;
		}
		setOptions(null);
	}, []);

	const handleConfirm = () => {
		try {
			options?.onConfirm?.();
		} catch (error) {
			console.error('Error in confirm onConfirm callback:', error);
		}
		close(true);
	};

	const handleCancel = () => {
		try {
			options?.onCancel?.();
		} catch (error) {
			console.error('Error in confirm onCancel callback:', error);
		}
		close(false);
	};

	const handleDismiss = () => {
		try {
			options?.onDismiss?.();
		} catch (error) {
			console.error('Error in confirm onDismiss callback:', error);
		}
		close(null);
	};

	const contextValue = useMemo(() => ({ confirm }), [confirm]);

	return (
		<ConfirmContext.Provider value={contextValue}>
			{children}

			{Boolean(options) && (
				<ConfirmModal
					{...options}
					onConfirm={handleConfirm}
					onCancel={handleCancel}
					onDismiss={handleDismiss}
				/>
			)}
		</ConfirmContext.Provider>
	);
};

// Modal
const ConfirmModal = ({
	title = 'Confirm',
	message,
	confirmLabel = 'Confirm',
	cancelLabel = 'Cancel',
	onConfirm,
	onCancel,
	onDismiss,
}) => {
	return (
		<StyledModal
			show
			onHide={onDismiss}
			backdrop="static"
			keyboard={false}
			aria-labelledby="confirm-title"
			aria-describedby="confirm-message">
			<Modal.Header closeButton>
				<Modal.Title id="confirm-title">{title}</Modal.Title>
			</Modal.Header>

			<Modal.Body id="confirm-message">{message}</Modal.Body>

			<Modal.Footer>
				<Button variant="secondary" onClick={onCancel}>
					{cancelLabel}
				</Button>

				<Button variant="success" onClick={onConfirm}>
					{confirmLabel}
				</Button>
			</Modal.Footer>
		</StyledModal>
	);
};

ConfirmModal.displayName = 'ConfirmModal'; // For debugging and DevTools clarity
